import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '@/components/feature/Navigation';
import Footer from '@/pages/home/components/Footer';
import SEOHead from '@/components/SEOHead';

const steps = [
  {
    number: '01',
    icon: 'ri-chat-smile-2-line',
    color: 'text-amber-400',
    bgColor: 'bg-amber-400/10',
    borderColor: 'border-amber-400/25',
    headline: 'You tell us what you need',
    tagline: 'No forms. No confusion. Just a conversation.',
    description:
      'Think of it like texting a friend who happens to be a medical expert. You tell us what\'s bothering you — "my tooth hurts", "I want a better smile", "I need implants" — and we take it from there. No complicated intake forms, no medical jargon.',
    details: [
      { icon: 'ri-whatsapp-line', text: 'Reach us on WhatsApp, email, or our website — whatever feels easiest' },
      { icon: 'ri-translate-2', text: 'We speak your language — English, Arabic, German, French, Russian & more' },
      { icon: 'ri-time-line', text: 'We respond within a few hours, not days' },
      { icon: 'ri-file-list-3-line', text: 'Send us any existing X-rays or reports — we\'ll review them for free' },
    ],
    image: 'https://public.readdy.ai/ai/img_res/25f04842374e778eace8331a17d3320d.jpg',
  },
  {
    number: '02',
    icon: 'ri-stethoscope-line',
    color: 'text-amber-300',
    bgColor: 'bg-amber-300/10',
    borderColor: 'border-amber-300/25',
    headline: 'We match you with the right doctor',
    tagline: 'Not just any doctor — your doctor.',
    description:
      'We don\'t send you to whoever is available. We look at your specific case, your treatment needs, and your preferences — then we hand-pick a certified specialist from our vetted network of Istanbul\'s top dental clinics. You get to review their profile before saying yes.',
    details: [
      { icon: 'ri-award-line', text: 'All doctors are board-certified with 10+ years of experience' },
      { icon: 'ri-hospital-line', text: 'Clinics are JCI-accredited — the gold standard in international healthcare' },
      { icon: 'ri-user-search-line', text: 'You see the doctor\'s credentials, reviews, and before/after cases first' },
      { icon: 'ri-shield-check-line', text: 'An independent supervisor reviews your treatment plan — not the treating doctor' },
    ],
    image: 'https://readdy.ai/api/search-image?query=professional%20doctor%20in%20white%20coat%20reviewing%20dental%20xray%20modern%20clinic%20dark%20moody%20lighting%20premium%20medical%20setting%20Istanbul%20Turkey%20confident%20specialist%20high%20end%20photography%20editorial%20style&width=600&height=500&seq=hiw-step2&orientation=landscape',
  },
  {
    number: '03',
    icon: 'ri-flight-takeoff-line',
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/25',
    headline: 'You fly in — we handle everything',
    tagline: 'Airport to clinic to hotel. All sorted.',
    description:
      'This is where most people are surprised. You don\'t have to figure out a single thing. We arrange your airport pickup, your hotel near the clinic, your appointment schedule, and even your meals if needed. You just show up — we do the rest.',
    details: [
      { icon: 'ri-car-line', text: 'Private airport transfer waiting for you when you land' },
      { icon: 'ri-hotel-line', text: 'Partner hotels within minutes of the clinic — negotiated rates' },
      { icon: 'ri-calendar-check-line', text: 'Your full treatment schedule planned before you even arrive' },
      { icon: 'ri-translate-2', text: 'A dedicated coordinator with you throughout your stay' },
    ],
    image: 'https://readdy.ai/api/search-image?query=Istanbul%20Turkey%20aerial%20view%20Bosphorus%20bridge%20golden%20hour%20sunset%20luxury%20travel%20destination%20premium%20photography%20dark%20moody%20editorial%20style%20cinematic%20wide%20angle&width=600&height=500&seq=hiw-step3&orientation=landscape',
  },
  {
    number: '04',
    icon: 'ri-hospital-line',
    color: 'text-amber-400',
    bgColor: 'bg-amber-400/10',
    borderColor: 'border-amber-400/25',
    headline: 'You get treated — fully supervised',
    tagline: 'Your doctor treats you. Our supervisor watches over everything.',
    description:
      'Here\'s what makes us different: while your dentist performs the treatment, an independent medical supervisor — who has no financial interest in your case — reviews every step. They\'re there to make sure everything is done correctly, safely, and in your best interest.',
    details: [
      { icon: 'ri-eye-line', text: 'Independent supervisor reviews your treatment plan before it starts' },
      { icon: 'ri-shield-user-line', text: 'You can ask questions directly to the supervisor at any time' },
      { icon: 'ri-camera-line', text: 'Full documentation of your treatment — photos, records, reports' },
      { icon: 'ri-check-double-line', text: 'Final quality check before you leave the clinic' },
    ],
    image: 'https://readdy.ai/api/search-image?query=modern%20dental%20clinic%20interior%20high%20tech%20equipment%20professional%20dentist%20at%20work%20dark%20moody%20lighting%20premium%20medical%20setting%20Istanbul%20Turkey%20editorial%20photography%20cinematic%20style&width=600&height=500&seq=hiw-step4&orientation=landscape',
  },
  {
    number: '05',
    icon: 'ri-heart-pulse-line',
    color: 'text-amber-300',
    bgColor: 'bg-amber-300/10',
    borderColor: 'border-amber-300/25',
    headline: 'We stay with you after you go home',
    tagline: 'The care doesn\'t stop when you board your flight.',
    description:
      'Most dental tourism companies disappear the moment you leave. We don\'t. Your doctor checks in with you after you\'re home, answers any questions, and monitors your recovery. If anything needs attention, we coordinate it — whether that\'s a remote consultation or a follow-up visit.',
    details: [
      { icon: 'ri-video-chat-line', text: 'Follow-up video consultations with your doctor included' },
      { icon: 'ri-message-3-line', text: 'Direct WhatsApp line to your coordinator for any concerns' },
      { icon: 'ri-file-list-3-line', text: 'Full treatment records sent to you and your local dentist' },
      { icon: 'ri-refresh-line', text: 'If a revision is ever needed, we coordinate it at no extra cost' },
    ],
    image: 'https://readdy.ai/api/search-image?query=person%20smiling%20confidently%20beautiful%20teeth%20portrait%20dark%20background%20warm%20golden%20lighting%20premium%20lifestyle%20photography%20after%20dental%20treatment%20happy%20satisfied%20editorial%20style&width=600&height=500&seq=hiw-step5&orientation=landscape',
  },
];

const faqs = [
  {
    q: 'How long does the whole process take?',
    a: 'Most treatments take 5–10 days in Istanbul. We\'ll give you an exact timeline once we review your case. Some treatments like veneers can be done in as little as 3 days.',
  },
  {
    q: 'Do I need to speak Turkish?',
    a: 'Not at all. Your coordinator speaks your language, and all our partner clinics have multilingual staff. You\'ll never feel lost.',
  },
  {
    q: 'What if something goes wrong after I go home?',
    a: 'We have a dedicated aftercare team. Your doctor is reachable via video call, and if a physical revision is needed, we coordinate it — usually at no extra cost depending on the situation.',
  },
  {
    q: 'Is it really safe to get dental work abroad?',
    a: 'Our partner clinics are JCI-accredited — the same international standard used by top hospitals worldwide. Plus, our independent supervision layer means there\'s an extra set of expert eyes on your case that most clinics don\'t offer.',
  },
  {
    q: 'How much can I actually save?',
    a: 'Typically 50–70% compared to UK, US, or Western European prices — even after flights and hotel. A full smile makeover that costs £15,000 in London often runs £4,000–£5,000 all-in through us.',
  },
  {
    q: 'What\'s the first step?',
    a: 'Just reach out. Send us a message on WhatsApp or fill out our booking form. We\'ll review your case for free and give you a clear picture of what\'s possible.',
  },
];

export default function HowItWorksPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const siteUrl = import.meta.env.VITE_SITE_URL || '';

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/how-it-works`,
      url: `${siteUrl}/how-it-works`,
      name: 'How It Works | Plexus Alliance — Dental Care in Turkey, Step by Step',
      description: 'See exactly how Plexus Alliance works — from your first message to your final follow-up. Doctor-supervised dental tourism in Istanbul, Turkey made simple.',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'How It Works', item: `${siteUrl}/how-it-works` },
        ],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: 'How to Get Doctor-Supervised Dental Treatment in Turkey',
      description: 'A step-by-step guide to getting safe, supervised dental care in Istanbul, Turkey through Plexus Alliance.',
      totalTime: 'P10D',
      step: steps.map((s, i) => ({
        '@type': 'HowToStep',
        position: i + 1,
        name: s.headline,
        text: s.description,
        url: `${siteUrl}/how-it-works#step-${s.number}`,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: { '@type': 'Answer', text: faq.a },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEOHead
        title="How It Works | Plexus Alliance — Dental Care in Turkey, Step by Step"
        description="See exactly how Plexus Alliance works — from your first message to your final follow-up. Doctor-supervised dental tourism in Istanbul, Turkey made simple."
        keywords="how dental tourism works, dental treatment Turkey process, supervised dental care Istanbul, dental implants Turkey steps"
        canonical={`${siteUrl}/how-it-works`}
        schema={schema}
      />
      <Navigation />

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-28 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://readdy.ai/api/search-image?query=Istanbul%20Turkey%20city%20skyline%20Bosphorus%20at%20dusk%20moody%20cinematic%20dark%20sky%20golden%20lights%20premium%20travel%20photography%20wide%20angle%20editorial%20style%20dramatic%20atmosphere&width=1400&height=700&seq=hiw-hero&orientation=landscape')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-[#0a0a0a]" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 text-center">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-10 h-px bg-amber-400/50" />
            <span className="text-amber-400/80 text-xs font-medium tracking-[0.25em] uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
              The Process
            </span>
            <div className="w-10 h-px bg-amber-400/50" />
          </div>
          <h1
            className="text-4xl md:text-7xl font-normal text-white leading-tight mb-6"
            style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.02em' }}
          >
            How It Works
          </h1>
          <p
            className="text-white/50 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-10"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            From your first message to your final check-up — here's exactly what happens, step by step. No surprises. No confusion.
          </p>
          {/* Step pills */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            {steps.map((s) => (
              <a
                key={s.number}
                href={`#step-${s.number}`}
                className="flex items-center gap-2 px-4 py-2 border border-white/10 hover:border-amber-400/40 text-white/40 hover:text-amber-400 text-xs tracking-widest uppercase transition-all duration-300 cursor-pointer rounded-full"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                <span className="text-amber-400/60">{s.number}</span>
                {s.headline.split(' ').slice(0, 3).join(' ')}...
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-10 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10 space-y-6 md:space-y-8">
          {steps.map((step, index) => (
            <div
              key={step.number}
              id={`step-${step.number}`}
              className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-0 border border-white/8 rounded-2xl overflow-hidden group hover:border-amber-400/20 transition-all duration-500`}
            >
              {/* Image side */}
              <div className="w-full md:w-[45%] relative overflow-hidden min-h-[280px] md:min-h-[420px]">
                <img
                  src={step.image}
                  alt={step.headline}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                {/* Big number overlay */}
                <div
                  className="absolute bottom-6 left-6 text-8xl md:text-9xl font-normal text-white/8 leading-none select-none"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {step.number}
                </div>
              </div>

              {/* Content side */}
              <div className="w-full md:w-[55%] bg-[#0f0f0f] p-8 md:p-12 flex flex-col justify-center">
                {/* Tag */}
                <div className="flex items-center gap-3 mb-6">
                  <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${step.bgColor} border ${step.borderColor}`}>
                    <i className={`${step.icon} text-base ${step.color}`}></i>
                  </div>
                  <span
                    className="text-xs text-white/30 tracking-[0.2em] uppercase"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    Step {step.number}
                  </span>
                </div>

                {/* Headline */}
                <h2
                  className="text-2xl md:text-4xl font-normal text-white mb-3 leading-snug"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {step.headline}
                </h2>

                {/* Tagline */}
                <p
                  className={`text-sm mb-5 font-medium ${step.color}`}
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {step.tagline}
                </p>

                {/* Description */}
                <p
                  className="text-white/50 text-base leading-relaxed mb-8"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {step.description}
                </p>

                {/* Detail bullets */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {step.details.map((detail, di) => (
                    <div key={di} className="flex items-start gap-3">
                      <div className={`w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-md ${step.bgColor}`}>
                        <i className={`${detail.icon} text-sm ${step.color}`}></i>
                      </div>
                      <p
                        className="text-white/40 text-sm leading-relaxed"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        {detail.text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Timeline summary strip */}
      <section className="py-16 md:py-24 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <h2
              className="text-3xl md:text-5xl font-normal text-white mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              The whole journey at a glance
            </h2>
            <p className="text-white/35 text-base" style={{ fontFamily: "'Inter', sans-serif" }}>
              Most patients complete their treatment in under 10 days.
            </p>
          </div>

          <div className="flex flex-col md:flex-row items-stretch gap-px bg-white/5 rounded-xl overflow-hidden">
            {[
              { day: 'Day 1', label: 'You reach out', icon: 'ri-chat-smile-2-line', color: 'text-amber-400' },
              { day: 'Day 2–3', label: 'Plan confirmed', icon: 'ri-file-check-line', color: 'text-amber-300' },
              { day: 'Day 4', label: 'You arrive in Istanbul', icon: 'ri-flight-land-line', color: 'text-amber-500' },
              { day: 'Day 5–8', label: 'Treatment happens', icon: 'ri-hospital-line', color: 'text-amber-400' },
              { day: 'Day 9', label: 'Final check & fly home', icon: 'ri-flight-takeoff-line', color: 'text-amber-300' },
              { day: 'Ongoing', label: 'Aftercare & follow-up', icon: 'ri-heart-pulse-line', color: 'text-amber-500' },
            ].map((item, i) => (
              <div key={i} className="flex-1 bg-[#111111] p-6 md:p-8 text-center group hover:bg-[#161616] transition-colors duration-300">
                <div className={`w-10 h-10 flex items-center justify-center rounded-full bg-white/5 mx-auto mb-4 ${item.color}`}>
                  <i className={`${item.icon} text-lg`}></i>
                </div>
                <p
                  className="text-amber-400/70 text-xs tracking-widest uppercase mb-2"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {item.day}
                </p>
                <p
                  className="text-white/70 text-sm font-medium"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {item.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-4 mb-5">
              <div className="w-8 h-px bg-amber-400/40" />
              <span className="text-amber-400/70 text-xs tracking-[0.25em] uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
                Common Questions
              </span>
              <div className="w-8 h-px bg-amber-400/40" />
            </div>
            <h2
              className="text-3xl md:text-5xl font-normal text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Still have questions?
            </h2>
          </div>

          <div className="space-y-2">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="border border-white/8 rounded-xl overflow-hidden hover:border-amber-400/20 transition-colors duration-300"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer group"
                >
                  <span
                    className="text-white/80 text-base font-medium group-hover:text-white transition-colors duration-200 pr-4"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {faq.q}
                  </span>
                  <div className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-full border border-white/10 group-hover:border-amber-400/30 transition-colors duration-200">
                    <i className={`text-sm text-white/40 group-hover:text-amber-400 transition-all duration-300 ${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-400 ${openFaq === i ? 'max-h-48' : 'max-h-0'}`}
                >
                  <p
                    className="px-6 pb-5 text-white/40 text-sm leading-relaxed"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {faq.a}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://readdy.ai/api/search-image?query=Istanbul%20Turkey%20Bosphorus%20bridge%20night%20lights%20reflection%20water%20dark%20moody%20cinematic%20premium%20travel%20photography%20wide%20angle%20editorial%20style&width=1400&height=600&seq=hiw-cta&orientation=landscape')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/75 to-black/90" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h2
            className="text-3xl md:text-6xl font-normal text-white mb-6 leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Ready to take the first step?
          </h2>
          <p
            className="text-white/45 text-lg mb-10 max-w-xl mx-auto leading-relaxed"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            It starts with a single message. Tell us what you need — we'll handle everything from there.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/book-treatment"
              className="flex items-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-10 py-4 font-semibold text-sm tracking-widest uppercase transition-all hover:scale-105 cursor-pointer whitespace-nowrap rounded-sm"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Start My Journey
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/treatments"
              className="flex items-center gap-3 border border-white/20 hover:border-amber-400/40 text-white/60 hover:text-amber-400 px-10 py-4 font-medium text-sm tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap rounded-sm"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Browse Treatments
            </Link>
          </div>
        </div>
      </section>

      <Footer onBookClick={() => window.REACT_APP_NAVIGATE('/book-treatment')} />
    </div>
  );
}
